/* ------------------------------------------------------------------
   Pulp — counting stat board
   ------------------------------------------------------------------
   Counts each figure once, when its board scrolls into view.

   Progressive enhancement, deliberately:
   • The finished figure is already the text in the Liquid output. This
     script never invents a number — it reads the target off data-count
     and animates toward the value the page already publishes, then
     writes that exact value back at the end.
   • The hidden/offset state only exists once this script has added
     .is-live. No JS, no IntersectionObserver, or a merchant with the
     section duplicated in the editor — the board simply renders.
   • prefers-reduced-motion skips the count and the stagger entirely.

   Re-runs on Shopify's section:load so it still animates when a
   merchant drags the section around in the theme editor.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var DUR = 1250;      // count duration, ms
  var STAGGER = 110;   // gap between tiles, ms — this is what makes it read as a board
  var reduce = window.matchMedia &&
               window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function fmt(value, decimals) {
    return decimals ? value.toFixed(decimals) : String(Math.round(value));
  }

  function count(node, delay) {
    var to = parseFloat(node.getAttribute('data-count'));
    if (isNaN(to)) return;

    var decimals = parseInt(node.getAttribute('data-dec') || '0', 10);
    var from = node.hasAttribute('data-from')
      ? parseFloat(node.getAttribute('data-from'))
      : 0;

    var settled = node.textContent;           // whatever Liquid published
    node.textContent = fmt(from, decimals);

    setTimeout(function () {
      var start = null;
      (function step(now) {
        if (start === null) start = now;
        var p = Math.min((now - start) / DUR, 1);
        var eased = 1 - Math.pow(1 - p, 3);   // ease-out cubic: decelerates in
        if (p < 1) {
          node.textContent = fmt(from + (to - from) * eased, decimals);
          requestAnimationFrame(step);
        } else {
          node.textContent = settled;         // land on the published string exactly
        }
      })(performance.now());
    }, delay);
  }

  function run(board) {
    if (board.getAttribute('data-ran')) return;
    board.setAttribute('data-ran', '1');

    all('.pulp__stat', board).forEach(function (cell, i) {
      var delay = reduce ? 0 : i * STAGGER;
      setTimeout(function () { cell.classList.add('is-in'); }, delay);
      if (!reduce) {
        all('[data-count]', cell).forEach(function (node) { count(node, delay); });
      }
    });
  }

  var observer = null;

  function init(root) {
    var boards = all('[data-ticker]', root).filter(function (b) {
      return !b.getAttribute('data-ran');
    });
    if (!boards.length) return;

    if (!('IntersectionObserver' in window)) {
      boards.forEach(run);
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            run(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.35 });
    }
    boards.forEach(function (board) {
      board.classList.add('is-live');
      observer.observe(board);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // theme editor: a re-rendered section arrives with a fresh DOM subtree
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
